import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import type { DynamicDialogRef } from 'primeng/dynamicdialog';

type ModalRegistration = {
  control: AbstractControl | undefined;
  transform: ((raw: unknown) => unknown) | undefined;
  buttonState: WritableSignal<Record<string, GrModalButtonRuntimeState>>;
};

export type GrModalButtonRuntimeState = {
  disabled?: boolean;
  loading?: boolean;
};

export interface GrModalRegisterFormOptions<TRaw = unknown, TOut = unknown> {
  readonly transform?: (raw: TRaw) => TOut;
}

@Injectable({ providedIn: 'root' })
export class GrModalService {
  private readonly registry = new WeakMap<DynamicDialogRef, ModalRegistration>();

  registerForm<TRaw = unknown, TOut = unknown>(
    ref: DynamicDialogRef,
    control: AbstractControl,
    options?: GrModalRegisterFormOptions<TRaw, TOut>,
  ): void {
    const entry = this.ensure(ref);
    entry.control = control;
    entry.transform = options?.transform as ((raw: unknown) => unknown) | undefined;
  }

  getControl<T extends AbstractControl = AbstractControl>(ref: DynamicDialogRef): T | undefined {
    return this.registry.get(ref)?.control as T | undefined;
  }

  getFormValue<T = unknown>(ref: DynamicDialogRef): T | undefined {
    const entry = this.registry.get(ref);
    const control = entry?.control;
    if (!control) return undefined;
    const raw =
      'getRawValue' in control && typeof control.getRawValue === 'function'
        ? (control as { getRawValue: () => unknown }).getRawValue()
        : control.value;
    if (entry?.transform) return entry.transform(raw) as T;
    return raw as T;
  }

  setButtonState(ref: DynamicDialogRef, key: string, state: GrModalButtonRuntimeState): void {
    const entry = this.ensure(ref);
    entry.buttonState.update((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...state },
    }));
  }

  getButtonState(ref: DynamicDialogRef, key: string): Signal<GrModalButtonRuntimeState> {
    const entry = this.ensure(ref);
    return computed(() => entry.buttonState()[key] ?? {});
  }

  unregister(ref: DynamicDialogRef): void {
    this.registry.delete(ref);
  }

  private ensure(ref: DynamicDialogRef): ModalRegistration {
    let entry = this.registry.get(ref);
    if (!entry) {
      entry = { control: undefined, transform: undefined, buttonState: signal({}) };
      this.registry.set(ref, entry);
    }
    return entry;
  }
}
